import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

export interface PartOption {
    id: string;
    partNo: string;
}

interface PartAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<PartOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    industryId?: string;
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

interface PartSearchResponse {
    data?: Array<{
        id: string;
        partNo: string;
    }>;
}

const PartAutocomplete = <M extends boolean = false, >({
                                                           industryId = undefined,
                                                           textFieldProps = {},
                                                           form = false,
                                                           name = undefined,
                                                           required = false,
                                                           setLoadingOptions: setLoadingOptionsProps,
                                                           ...props
                                                       }: PartAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches part options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (matches against part numbers)
     * @returns Array of matching part options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.autoCompletePartLookup(keywords, industryId, cancelToken) as PartSearchResponse;
            if (!isMounted())
                return [];
            return response?.data?.map(e => ({
                ...e,
                id: e.id,
                partNo: e.partNo,
            })) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [industryId, isMounted]);

    /**
     * Determines if two part options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: PartOption, value: PartOption | null) =>
        option.id === value?.id, []);

    /**
     * Gets the display label for a part option (part number)
     */
    const getOptionLabel = useCallback((option: PartOption) => option?.partNo ?? "", []);

    /**
     * Gets the unique key for a part option
     */
    const getOptionKey = useCallback((option: PartOption) => option?.id ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<PartOption, M>
            keywordsBased
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default PartAutocomplete; 